import { CallNoteOutSchema } from "@/api/client";
import { handleError } from "@/api/handleError";
import useAnalytics from "@/composables/useAnalytics";
import { trackAmplitudeEvent } from "@/helpers/integrations/analytics/amplitude";
import { AMPLITUDE_EVENTS } from "@/helpers/integrations/analytics/events";
import { fetchCallLogDetailsById } from "@/helpers/queries";
import {
  addNewCallNote,
  addNewCallNoteToActiveCall,
  updateCallNote,
} from "@/helpers/queries/call-notes";
import {
  ActiveCallNote,
  CallNoteCreateFromLogs,
  CallNoteUpdateFromLogs,
} from "@/helpers/types/notes";
import { notify } from "@kyvg/vue3-notification";
import { defineStore } from "pinia";
import { useCallStore } from "./call.store";

interface NoteState {
  activeCallNote?: ActiveCallNote | null;
  setNoteCounter: boolean;
  callnote: string;
  activeCallId?: number | null;
  showNoteButton: boolean;
}

export const useNotesStore = defineStore("note", {
  state: (): NoteState => ({
    activeCallNote: null,
    setNoteCounter: false,
    callnote: "",
    activeCallId: null,
    showNoteButton: false,
  }),
  getters: {},
  actions: {
    async saveCallNote(
      note: string,
      noteObject:
        | CallNoteOutSchema
        | CallNoteCreateFromLogs
        | CallNoteUpdateFromLogs
        | ActiveCallNote,
      idForUpdate: number | undefined = undefined
    ) {
      const callStore = useCallStore();
      noteObject.note = note;
      noteObject = {
        ...noteObject,
        ...(idForUpdate ? { id: idForUpdate } : {}),
      };

      const analytics = useAnalytics();
      void analytics.logEvent(AMPLITUDE_EVENTS.CALL_NOTE_SAVE_ATTEMPTED);

      try {
        if (idForUpdate) {
          const response = await updateCallNote(
            {
              note: noteObject.note,
            },
            idForUpdate
          );

          this.showNoteButton = false;

          return response.data;
        } else {
          const hasCallId =
            "call_log_id" in noteObject && noteObject.call_log_id;

          const createdNote = await addNewCallNote(noteObject, hasCallId);

          if (!callStore.selectedCallInfo?.id) {
            throw new Error("Call id not found");
          }

          if (!callStore.selectedCallInfo?.id) {
            return;
          }
          const response = await fetchCallLogDetailsById(
            String(callStore.selectedCallInfo?.id)
          );

          callStore.selectedCallInfo = { ...response.data };

          void trackAmplitudeEvent(AMPLITUDE_EVENTS.CALL_NOTE_SAVED);

          return createdNote.data;
        }
      } catch (e) {
        handleError({ error: e, fallbackMessage: "Error saving note" });
        throw e;
      }
    },
    // Active call note
    async createNewNote() {
      const payload = {
        ...this.activeCallNote,
        note: this.callnote,
      };

      const response = await addNewCallNoteToActiveCall(payload);

      this.activeCallId = response.data?.id;
      this.setNoteCounter = true;

      notify({
        text: "Note saved successfully",
        type: "success",
      });

      return this.activeCallId;
    },
    async updateNote() {
      const callStore = useCallStore();
      if (this.activeCallId) {
        const response = await updateCallNote(
          { note: this.callnote },
          this.activeCallId
        );
        if (callStore.transientCallRecord) {
          callStore.transientCallRecord.note = response.data;
        }
        notify({
          text: "Note saved successfully",
          type: "success",
        });

        await trackAmplitudeEvent(AMPLITUDE_EVENTS.CALL_NOTE_SAVED);
      }
    },
    async saveActiveCallNote() {
      if (this.callnote) {
        try {
          // If save has been clicked, update the note
          if (this.setNoteCounter) {
            await this.updateNote();
          } else {
            // If save hasn't been clicked, call both
            await this.createNewNote();
            await this.updateNote();
          }
        } catch (error) {
          handleError({
            error,
            fallbackMessage: "Error saving active call note",
          });
        }
      }
    },
  },
});
